import React from 'react'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon/Icon'
import { Tenant } from '../../Interfaces/Types'
import { useConfig } from '../Config/ConfigContext'

// Per-tenant monitoring (docs/grafana-tenant-monitoring.md). Renders the
// "Grafana Dashboard" button on the tenant page: it opens the tenant's Grafana
// dashboard in a new tab, scoped to this tenant.
//
// Grafana instance and dashboard come from /api/clientappconfig
// (ClientApp:GrafanaUrl + ClientApp:GrafanaTenantDashboardPath, TMA-labelled App
// Configuration keys), so nonproduction and production reach their own Grafana
// without the frontend knowing either. Nothing is rendered while either is unset.

// The URL scopes the dashboard to this tenant through Grafana's `var-<name>`
// query parameters: `tenant` carries the tenant name, `environment` the stage
// (alpha, stable-alpha, beta, production) so the dashboard can narrow its
// queries. Grafana ignores variables a dashboard does not define.
export const buildGrafanaTenantUrl = (
  grafanaUrl: string,
  dashboardPath: string,
  tenant: Pick<Tenant, 'tenantName' | 'environment'>
): string => {
  const base = grafanaUrl.replace(/\/+$/, '')
  const path = dashboardPath.startsWith('/') ? dashboardPath : `/${dashboardPath}`
  const params = new URLSearchParams({ orgId: '1', 'var-tenant': tenant.tenantName, 'var-environment': tenant.environment })
  return `${base}${path}?${params.toString()}`
}

interface TenantMonitoringProps {
  tenant: Tenant
}

const TenantMonitoring: React.FC<TenantMonitoringProps> = ({ tenant }) => {
  const config = useConfig()

  const grafanaUrl = config?.grafanaUrl?.trim()
  const dashboardPath = config?.grafanaTenantDashboardPath?.trim()
  if (!grafanaUrl || !dashboardPath) {
    return null
  }

  const url = buildGrafanaTenantUrl(grafanaUrl, dashboardPath, tenant)

  return (
    <Button2
      style={{ width: '100%', justifyContent: 'center' }}
      buttonSize='30px'
      buttonRadius='8px'
      variant='outlined'
      leftElement={<Icon iconName='DataFileGraph' size={16} />}
      text='Grafana Dashboard'
      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
    />
  )
}

export default TenantMonitoring
